import { Transition } from "@headlessui/react"
import { Head, Link, useForm, usePage } from "@inertiajs/react"
import type { FormEvent } from "react"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import { identityEmailVerificationPath, settingsEmailPath } from "@/routes"
import type { BreadcrumbItem, SharedData } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Email settings",
    href: settingsEmailPath(),
  },
]

export default function Email() {
  const { auth } = usePage<SharedData>().props
  const {
    data,
    setData,
    patch,
    processing,
    errors,
    recentlySuccessful,
    reset,
  } = useForm({
    email: auth.user.email,
    password_challenge: "",
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    patch(settingsEmailPath(), {
      preserveScroll: true,
      onSuccess: () => reset("password_challenge"),
      onError: () => reset("password_challenge"),
    })
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <HeadingSmall
            title="Update email"
            description="Update your email address and verify it"
          />

          <form onSubmit={submit} className="space-y-6">
            <div className="grid gap-2">
              <Label htmlFor="email">Email address</Label>

              <Input
                id="email"
                type="email"
                className="mt-1 block w-full"
                required
                autoComplete="username"
                placeholder="Email address"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
              />

              <InputError className="mt-2" messages={errors.email} />
            </div>

            {!auth.user.verified && (
              <div>
                <p className="text-muted-foreground -mt-4 text-sm">
                  Your email address is unverified.{" "}
                  <Link
                    href={identityEmailVerificationPath()}
                    method="post"
                    as="button"
                    className="text-foreground underline decoration-neutral-300 underline-offset-4 transition-colors duration-300 ease-out hover:decoration-current! dark:decoration-neutral-500"
                  >
                    Click here to resend the verification email.
                  </Link>
                </p>
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="password_challenge">Current password</Label>

              <Input
                id="password_challenge"
                type="password"
                className="mt-1 block w-full"
                autoComplete="current-password"
                placeholder="Current password"
                value={data.password_challenge}
                onChange={(e) => setData("password_challenge", e.target.value)}
              />

              <InputError messages={errors.password_challenge} />
            </div>

            <div className="flex items-center gap-4">
              <Button type="submit" disabled={processing}>
                Save
              </Button>

              <Transition
                show={recentlySuccessful}
                enter="transition ease-in-out"
                enterFrom="opacity-0"
                leave="transition ease-in-out"
                leaveTo="opacity-0"
              >
                <p className="text-sm text-neutral-600">Saved</p>
              </Transition>
            </div>
          </form>
        </div>
      </SettingsLayout>
    </AppLayout>
  )
}
